import type { OtsOnTimeQuickFilter } from '@/utils/otsFlagOverride'
import type { OtsReasoningResponse } from '@/utils/otsReasoning'
import { apiService } from './api'
import type { ApiResponse, PaginatedResponse } from '@/types/api'
import type {
  OtsDailyLeadTimeBreakdown,
  OtsWeeklyLeadTimeBreakdown,
  OtsJobTypeShippedSalesResponse,
  OtsMonthlyMetricRow,
  PrintIqKpiResponse,
  ReportCardDay,
  ReportCardResponse,
} from '@/types/printiqKpi'

// PIQ Live Connection types matching the API spec
export interface PIQLiveConnection {
  host: string
  port: string
  database: string
  connection_time_ms: number
}

export interface PIQLiveServer {
  version: string
}

export interface PIQLiveExtensions {
  pdo_sqlsrv: boolean
  sqlsrv: boolean
}

export interface PIQLiveSuccessResponse {
  status: 'success'
  message: string
  connection: PIQLiveConnection
  server: PIQLiveServer
  extensions: PIQLiveExtensions
  timestamp: string
}

/** Export criteria for OTS CSV: date_dispatched (default), date_due, date_customer_required, all */
export type OtsExportCriteria = 'date_dispatched' | 'date_due' | 'date_customer_required' | 'all'

/** OTS order population: standard/custom (default), series, or combined (weighted on-time rate over summed counts). */
export type OtsOrderPopulation = 'standard_custom' | 'series' | 'combined'

/** Details tab quick filter: on-time only, late only, or both (default, no filter). */

/** Manual on-time flag change row (GET timelines). */
export type OtsOnTimeFlagTimelineEntry = {
  id: number
  sales_history_uuid: string
  job_no: string | null
  previous_value: number | null
  new_value: number
  category: string | null
  subcategory: string | null
  department: string | null
  reason: string | null
  created_at: string | null
  user: { id: number; name: string; email: string } | null
}

/**
 * Axios validateStatus treats 403/422 as success; Laravel then returns JSON without success:true.
 * Throw so callers hit catch and show a toast.
 */
function assertOtsPiqJsonSuccess(data: unknown, fallbackMessage: string): void {
  if (
    data === null ||
    typeof data !== 'object' ||
    !('success' in data) ||
    (data as { success?: boolean }).success !== true
  ) {
    const msg =
      typeof (data as { message?: string }).message === 'string' &&
      (data as { message: string }).message.length > 0
        ? (data as { message: string }).message
        : fallbackMessage
    throw new Error(msg)
  }
}

export interface PIQLiveErrorResponse {
  status: 'error'
  message: string
  error?: string
  error_code?: string
  required?: string[]
  optional?: Record<string, string>
  configured?: {
    host?: string
    port?: string
    database?: string
    username?: string
    password?: string
  }
  extensions_loaded?: {
    pdo_sqlsrv?: boolean
    sqlsrv?: boolean
  }
  connection_details?: {
    host?: string
    port?: string
    database?: string
  }
  troubleshooting?: Record<string, string>
  type?: string
}

export type PIQLiveResponse = PIQLiveSuccessResponse | PIQLiveErrorResponse

export interface Machine {
  uuid: string
  machine: string | null
  nickname: string | null
  brand: string | null
  model: string | null
  serial_number: string | null
  tech_website: string | null
  tech_email: string | null
  tech_phone_number: string | null
  extra_contact_info: string | null
  max_width: string | null
  max_length: string | null
  additional_notes: string | null
  type: string | null
  factory_location: string | null
  department: string | null
  // Latest machine status derived from most recent ActivityLog entry
  // green = working normally, yellow = issue but usable, red = unusable/shutdown
  latest_status?: 'green' | 'yellow' | 'red' | null
  created_at?: string
  updated_at?: string
}

export interface MachineFilters {
  search?: string
  brand?: string
  model?: string
  /** Factory/location filter: EVT (Everett), LAN (Lancaster). Omit for all. */
  factory_location?: 'EVT' | 'LAN'
  page?: number
  per_page?: number
}

export interface MachineImportResponse {
  status: 'success' | 'error'
  message: string
  data: {
    created: number
    updated: number
    skipped: number
    errors: string[]
  }
}

/**
 * Map stored/API factory values to Manage List form select values (EVT | LAN).
 * DB and CSV often use "Everett" / "Lancaster"; the form only lists EVT / LAN.
 */
export function normalizeMachineFactoryLocationForForm(
  raw: string | null | undefined
): 'EVT' | 'LAN' | null {
  if (raw == null) return null
  const s = String(raw).trim()
  if (!s) return null
  const u = s.toUpperCase()
  if (u === 'EVT' || u === 'EVERETT') return 'EVT'
  if (u === 'LAN' || u === 'LANCASTER') return 'LAN'
  return null
}

/** Resolve factory for API list filter (machines index only understands EVT | LAN). */
export function machineFactoryToListFilter(
  raw: string | null | undefined
): 'EVT' | 'LAN' | '' {
  const n = normalizeMachineFactoryLocationForForm(raw)
  return n ?? ''
}

/** Payload for create/update machine (brand and model required for create) */
export interface MachineCreateUpdatePayload {
  machine?: string | null
  nickname?: string | null
  brand: string
  model: string
  serial_number?: string | null
  type?: string | null
  factory_location?: string | null
  department?: string | null
  tech_website?: string | null
  tech_email?: string | null
  tech_phone_number?: string | null
  extra_contact_info?: string | null
  additional_notes?: string | null
}

class MachineService {
  private readonly endpoint = '/machines'

  /**
   * Get paginated list of machines
   */
  async getMachines(filters?: MachineFilters): Promise<PaginatedResponse<Machine>> {
    const params: Record<string, string | number> = {}
    
    if (filters?.search) params.search = filters.search
    if (filters?.brand) params.brand = filters.brand
    if (filters?.model) params.model = filters.model
    if (filters?.factory_location) params.factory_location = filters.factory_location
    if (filters?.page) params.page = filters.page
    if (filters?.per_page) params.per_page = filters.per_page

    return await apiService.getPaginated<Machine>(this.endpoint, params)
  }

  /**
   * Get a single machine by UUID (for prefilling Update form).
   */
  async getMachine(uuid: string): Promise<Machine> {
    const response = await apiService.get<Machine>(`${this.endpoint}/${uuid}`)
    return response
  }

  /**
   * Create a new machine.
   */
  async createMachine(data: MachineCreateUpdatePayload): Promise<Machine> {
    const response = await apiService.post<Machine>(this.endpoint, data)
    return response
  }

  /**
   * Update an existing machine.
   */
  async updateMachine(uuid: string, data: Partial<MachineCreateUpdatePayload>): Promise<Machine> {
    const response = await apiService.put<Machine>(`${this.endpoint}/${uuid}`, data)
    return response
  }

  /**
   * Delete a machine (soft delete).
   */
  async deleteMachine(uuid: string): Promise<void> {
    await apiService.delete(`${this.endpoint}/${uuid}`)
  }

  /**
   * Export machines to CSV
   * 
   * Backend endpoint: GET /api/machines/export-csv
   * Returns a CSV file download
   */
  async exportCsv(): Promise<void> {
    const axiosInstance = (apiService as any).api
    
    const response = await axiosInstance.get(
      `${this.endpoint}/export-csv`,
      {
        responseType: 'blob',
      }
    )

    // Create blob and trigger download
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    
    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition']
    let filename = 'machines_export.csv'
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }
    
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    // Clean up the URL object
    URL.revokeObjectURL(url)
  }

  /**
   * Import machines from CSV file
   * 
   * Backend endpoint: POST /api/machines/import-csv
   * Backend expects:
   * - file: File (required, CSV or TXT)
   * - update_existing: boolean (default: true) - sent as FormData field 'update_existing'
   * 
   * Uses extended timeout (30 minutes) for large file imports
   */
  async importCsv(
    file: File,
    updateExisting: boolean = true,
    onUploadProgress?: (progress: number) => void
  ): Promise<MachineImportResponse> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('update_existing', updateExisting ? 'true' : 'false')

    const axiosInstance = (apiService as any).api
    const response = await axiosInstance.post(
      `${this.endpoint}/import-csv`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        // CRITICAL: Extended timeout for large CSV imports (30 minutes)
        timeout: 1800000, // 30 minutes in milliseconds
        onUploadProgress: (event: any) => {
          if (onUploadProgress && event.total) {
            const progress = Math.round((event.loaded * 100) / event.total)
            onUploadProgress(progress)
          }
        }
      }
    )

    return response.data
  }

  /**
   * Download CSV template for machine import
   * 
   * Backend endpoint: GET /api/machines/import-template
   * Returns a CSV file download
   */
  async downloadImportTemplate(): Promise<void> {
    const axiosInstance = (apiService as any).api
    
    const response = await axiosInstance.get(
      `${this.endpoint}/import-template`,
      {
        responseType: 'blob',
      }
    )

    // Extract filename from Content-Disposition header or generate with RUKHERP_ prefix
    const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition']
    const date = new Date()
    const dateStr = `${date.getFullYear()}_${String(date.getMonth() + 1).padStart(2, '0')}_${String(date.getDate()).padStart(2, '0')}`
    let filename = `RUKHERP_${dateStr}_machines_import_template.csv` // Fallback
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        // Remove quotes if present
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }

    // Create blob and trigger download
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    // Clean up the URL object
    URL.revokeObjectURL(url)
  }

  /**
   * Test PIQ SQL Server connection
   * 
   * Backend endpoint: GET /api/piq/live
   * Returns connection status and details according to the API spec
   * 
   * Note: apiService.get() returns response.data directly, so this returns PIQLiveResponse directly
   */
  async testPiqConnection(): Promise<PIQLiveResponse> {
    return await apiService.get<PIQLiveResponse>('/piq/live')
  }

  /**
   * Single endpoint for PrintIQ OTS KPI metrics.
   * GET /api/piq/kpi — no params: returns cached snapshot (top bar, default).
   * GET /api/piq/kpi?date_from=&date_to=&factory= — with params: returns cache when same as snapshot, else live.
   * Default page load should use same params as snapshot (from first response date_from/date_to).
   */
  async getPrintIqKpi(params?: { date_from?: string; date_to?: string; factory?: 'both' | 'EVT' | 'LAN'; delivery_types?: string[]; reference8?: string[]; exclude_statuses?: string[]; order_population?: OtsOrderPopulation }): Promise<PrintIqKpiResponse> {
    if (params == null || params.date_from == null || params.date_to == null) {
      // No complete date range: cached current-month snapshot. Still forward order_population so
      // the nav bar can select the Standard/Custom, Series, or Combined cached snapshot.
      const cacheParams: Record<string, string> = {}
      if (params?.order_population != null && params.order_population !== 'standard_custom') {
        cacheParams.order_population = params.order_population
      }
      return await apiService.get<PrintIqKpiResponse>('/piq/kpi', { params: cacheParams })
    }
    const requestParams: Record<string, string | string[]> = {
      date_from: params.date_from,
      date_to: params.date_to,
    }
    if (params.factory != null && params.factory !== 'both') {
      requestParams.factory = params.factory
    }
    if (params.delivery_types != null && params.delivery_types.length > 0) {
      requestParams.delivery_types = params.delivery_types
    }
    if (params.reference8 != null && params.reference8.length > 0) {
      requestParams.reference8 = params.reference8
    }
    if (params.exclude_statuses != null && params.exclude_statuses.length >= 0) {
      requestParams.exclude_statuses = params.exclude_statuses
    }
    if (params.order_population != null && params.order_population !== 'standard_custom') {
      requestParams.order_population = params.order_population
    }
    return await apiService.get<PrintIqKpiResponse>('/piq/kpi', {
      params: requestParams,
      timeout: 300000, // 5 min for live PIQ
    })
  }

  /**
   * Report Card: single day or date range (experimental).
   * Single: GET /api/piq/report-card?date=YYYY-MM-DD&factory=...
   * Range:  GET /api/piq/report-card?date_from=...&date_to=...&factory=...
   */
  async getReportCard(params: {
    date?: string
    date_from?: string
    date_to?: string
    factory?: 'both' | 'EVT' | 'LAN'
    delivery_types?: string[]
    reference8?: string[]
    exclude_statuses?: string[]
    /** When false, range response omits heavy per_day (fetch via getReportCardPerDay). Default: true. */
    include_per_day?: boolean
    order_population?: OtsOrderPopulation
  }): Promise<ReportCardResponse> {
    const requestParams: Record<string, string | string[]> = {
      factory: params.factory ?? 'both',
    }
    if (params.date_from != null && params.date_to != null) {
      requestParams.date_from = params.date_from
      requestParams.date_to = params.date_to
    } else if (params.date != null) {
      requestParams.date = params.date
    }
    if (params.delivery_types != null && params.delivery_types.length > 0) {
      requestParams.delivery_types = params.delivery_types
    }
    if (params.reference8 != null && params.reference8.length > 0) {
      requestParams.reference8 = params.reference8
    }
    if (params.exclude_statuses != null && params.exclude_statuses.length >= 0) {
      requestParams.exclude_statuses = params.exclude_statuses
    }
    if (params.include_per_day === false) {
      requestParams.include_per_day = '0'
    }
    if (params.order_population != null && params.order_population !== 'standard_custom') {
      requestParams.order_population = params.order_population
    }
    return await apiService.get<ReportCardResponse>('/piq/report-card', {
      params: requestParams,
    })
  }

  /**
   * Per-day report card rows only (second request after fast range summary). GET /api/piq/report-card?per_day_only=1
   */
  async getReportCardPerDay(params: {
    date_from: string
    date_to: string
    factory?: 'both' | 'EVT' | 'LAN'
    delivery_types?: string[]
    reference8?: string[]
    exclude_statuses?: string[]
    order_population?: OtsOrderPopulation
  }): Promise<{ per_day: ReportCardDay[]; fetched_at: string }> {
    const requestParams: Record<string, string | string[]> = {
      factory: params.factory ?? 'both',
      date_from: params.date_from,
      date_to: params.date_to,
      per_day_only: '1',
    }
    if (params.delivery_types != null && params.delivery_types.length > 0) {
      requestParams.delivery_types = params.delivery_types
    }
    if (params.reference8 != null && params.reference8.length > 0) {
      requestParams.reference8 = params.reference8
    }
    if (params.exclude_statuses != null && params.exclude_statuses.length >= 0) {
      requestParams.exclude_statuses = params.exclude_statuses
    }
    if (params.order_population != null && params.order_population !== 'standard_custom') {
      requestParams.order_population = params.order_population
    }
    return await apiService.get('/piq/report-card', {
      params: requestParams,
    })
  }

  /**
   * Get On Time Shipping list from sales_history (paginated).
   * Backend endpoint: GET /api/piq/ots-list?date_from=...&date_to=...&page=...&per_page=...
   * Uses extended timeout (5 min) for large date ranges.
   */
  async getOtsList(params: {
    date_from: string
    date_to: string
    reference8?: string[]
    delivery_types?: string[]
    exclude_statuses?: string[]
    factory?: 'both' | 'EVT' | 'LAN'
    page?: number
    per_page?: number
    /** Server-side LIKE filter on job / customer / order fields */
    search?: string
    /** 'all' = show every population together (Details "show everything"). */
    order_population?: OtsOrderPopulation | 'all'
    /** Details tab quick filter; omitted/'both' = no filter. */
    on_time?: OtsOnTimeQuickFilter
    /** OTS Reasoning drill-through: exact match on the line's latest override. */
    category?: string
    subcategory?: string
    department?: string
  }): Promise<{
    success: boolean
    data: Record<string, unknown>[]
    message?: string
    pagination?: { current_page: number; last_page: number; per_page: number; total: number }
  }> {
    const url = '/piq/ots-list'
    const requestParams: Record<string, string | string[] | number> = {
      date_from: params.date_from,
      date_to: params.date_to,
    }
    if (params.reference8 != null) {
      requestParams.reference8 = params.reference8
    }
    if (params.delivery_types != null && params.delivery_types.length > 0) {
      requestParams.delivery_types = params.delivery_types
    }
    if (params.factory != null && params.factory !== 'both') {
      requestParams.factory = params.factory
    }
    if (params.exclude_statuses != null && params.exclude_statuses.length >= 0) {
      requestParams.exclude_statuses = params.exclude_statuses
    }
    if (params.page != null) {
      requestParams.page = params.page
    }
    if (params.per_page != null) {
      requestParams.per_page = params.per_page
    }
    if (params.search != null && params.search.trim() !== '') {
      requestParams.search = params.search.trim()
    }
    if (params.order_population != null && params.order_population !== 'standard_custom') {
      requestParams.order_population = params.order_population
    }
    if (params.on_time != null && params.on_time !== 'both') {
      requestParams.on_time = params.on_time
    }
    if (params.category != null && params.category !== '') {
      requestParams.category = params.category
    }
    if (params.subcategory != null && params.subcategory !== '') {
      requestParams.subcategory = params.subcategory
    }
    if (params.department != null && params.department !== '') {
      requestParams.department = params.department
    }
    const config = {
      params: requestParams,
      timeout: 300000, // 5 minutes – can be slow for large ranges
    }
    return await apiService.get<{
      success: boolean
      data: Record<string, unknown>[]
      message?: string
      pagination?: { current_page: number; last_page: number; per_page: number; total: number }
    }>(url, config)
  }

  /**
   * Get all OTS rows for current filters (no pagination) for CSV export.
   * Same endpoint as getOtsList with export=1. Optional export_criteria for modal options.
   */
  async getOtsListExport(params: {
    date_from: string
    date_to: string
    reference8?: string[]
    delivery_types?: string[]
    exclude_statuses?: string[]
    factory?: 'both' | 'EVT' | 'LAN'
    export_criteria?: OtsExportCriteria
    order_population?: OtsOrderPopulation | 'all'
    search?: string
    /** Details tab quick filter; omitted/'both' = no filter. */
    on_time?: OtsOnTimeQuickFilter
    /** OTS Reasoning drill-through: exact match on the line's latest override. */
    category?: string
    subcategory?: string
    department?: string
  }): Promise<{ success: boolean; data: Record<string, unknown>[]; message?: string }> {
    const url = '/piq/ots-list'
    const requestParams: Record<string, string | string[] | number | boolean> = {
      date_from: params.date_from,
      date_to: params.date_to,
      export: 1,
    }
    if (params.reference8 != null) {
      requestParams.reference8 = params.reference8
    }
    if (params.delivery_types != null && params.delivery_types.length > 0) {
      requestParams.delivery_types = params.delivery_types
    }
    if (params.exclude_statuses != null && params.exclude_statuses.length >= 0) {
      requestParams.exclude_statuses = params.exclude_statuses
    }
    if (params.factory != null && params.factory !== 'both') {
      requestParams.factory = params.factory
    }
    if (params.export_criteria != null) {
      requestParams.export_criteria = params.export_criteria
    }
    if (params.order_population != null && params.order_population !== 'standard_custom') {
      requestParams.order_population = params.order_population
    }
    if (params.search != null && params.search.trim() !== '') {
      requestParams.search = params.search.trim()
    }
    if (params.on_time != null && params.on_time !== 'both') {
      requestParams.on_time = params.on_time
    }
    if (params.category != null && params.category !== '') {
      requestParams.category = params.category
    }
    if (params.subcategory != null && params.subcategory !== '') {
      requestParams.subcategory = params.subcategory
    }
    if (params.department != null && params.department !== '') {
      requestParams.department = params.department
    }
    return await apiService.get<{ success: boolean; data: Record<string, unknown>[]; message?: string }>(url, {
      params: requestParams,
      timeout: 300000,
    })
  }

  /**
   * Get OTS metrics aggregated by month for the given year (Jan through current month).
   * Backend endpoint: GET /api/piq/ots-metrics/monthly?year=...&reference8[]=...&delivery_types[]=...&factory=...
   */
  async getOtsMonthlyMetrics(params: {
    year: number
    reference8?: string[]
    delivery_types?: string[]
    exclude_statuses?: string[]
    factory?: 'both' | 'EVT' | 'LAN'
    order_population?: OtsOrderPopulation
  }): Promise<OtsMonthlyMetricRow[]> {
    const requestParams: Record<string, string | string[] | number> = {
      year: params.year,
    }
    if (params.reference8 != null && params.reference8.length > 0) {
      requestParams.reference8 = params.reference8
    }
    if (params.delivery_types != null && params.delivery_types.length > 0) {
      requestParams.delivery_types = params.delivery_types
    }
    if (params.exclude_statuses != null && params.exclude_statuses.length >= 0) {
      requestParams.exclude_statuses = params.exclude_statuses
    }
    if (params.factory != null && params.factory !== 'both') {
      requestParams.factory = params.factory
    }
    if (params.order_population != null && params.order_population !== 'standard_custom') {
      requestParams.order_population = params.order_population
    }
    return await apiService.get<OtsMonthlyMetricRow[]>('/piq/ots-metrics/monthly', {
      params: requestParams,
    })
  }

  /**
   * Daily average days to ship: combined, EVT, and LAN (External scorecard cohort).
   * Backend: GET /api/piq/ots-metrics/daily-lead-time
   */
  async getOtsDailyLeadTime(params: {
    cohort: 'standard' | 'custom'
    date_from: string
    date_to: string
    reference8?: string[]
    delivery_types?: string[]
    exclude_statuses?: string[]
    order_population?: OtsOrderPopulation
  }): Promise<OtsDailyLeadTimeBreakdown> {
    const requestParams: Record<string, string | string[]> = {
      cohort: params.cohort,
      date_from: params.date_from,
      date_to: params.date_to,
    }
    if (params.reference8 != null && params.reference8.length > 0) {
      requestParams.reference8 = params.reference8
    }
    if (params.delivery_types != null && params.delivery_types.length > 0) {
      requestParams.delivery_types = params.delivery_types
    }
    if (params.exclude_statuses != null && params.exclude_statuses.length >= 0) {
      requestParams.exclude_statuses = params.exclude_statuses
    }
    if (params.order_population != null && params.order_population !== 'standard_custom') {
      requestParams.order_population = params.order_population
    }
    return await apiService.get<OtsDailyLeadTimeBreakdown>('/piq/ots-metrics/daily-lead-time', {
      params: requestParams,
      timeout: 120000,
    })
  }

  /**
   * Weekly average days to ship (Monday→Sunday, newest week first): combined, EVT, and LAN.
   * Backend: GET /api/piq/ots-metrics/weekly-lead-time
   */
  async getOtsWeeklyLeadTime(params: {
    cohort?: 'standard'
    weeks?: number
    reference8?: string[]
    delivery_types?: string[]
    exclude_statuses?: string[]
    order_population?: OtsOrderPopulation
  }): Promise<OtsWeeklyLeadTimeBreakdown> {
    const requestParams: Record<string, string | string[] | number> = {
      cohort: params.cohort ?? 'standard',
    }
    if (params.weeks != null) {
      requestParams.weeks = params.weeks
    }
    if (params.reference8 != null && params.reference8.length > 0) {
      requestParams.reference8 = params.reference8
    }
    if (params.delivery_types != null && params.delivery_types.length > 0) {
      requestParams.delivery_types = params.delivery_types
    }
    if (params.exclude_statuses != null && params.exclude_statuses.length >= 0) {
      requestParams.exclude_statuses = params.exclude_statuses
    }
    if (params.order_population != null && params.order_population !== 'standard_custom') {
      requestParams.order_population = params.order_population
    }
    return await apiService.get<OtsWeeklyLeadTimeBreakdown>('/piq/ots-metrics/weekly-lead-time', {
      params: requestParams,
      timeout: 120000,
    })
  }

  /**
   * Sum of total_sale (cents) by canonical Job Type (job_type_filter) for lines with DaysToShip set.
   * List Job Type checkboxes are not applied. GET /api/piq/ots-metrics/job-type-shipped-sales
   */
  async getOtsJobTypeShippedSales(params: {
    date_from: string
    date_to: string
    delivery_types?: string[]
    exclude_statuses?: string[]
    factory?: 'both' | 'EVT' | 'LAN'
    order_population?: OtsOrderPopulation
  }): Promise<OtsJobTypeShippedSalesResponse> {
    const requestParams: Record<string, string | string[]> = {
      date_from: params.date_from,
      date_to: params.date_to,
    }
    if (params.delivery_types != null && params.delivery_types.length > 0) {
      requestParams.delivery_types = params.delivery_types
    }
    if (params.exclude_statuses != null && params.exclude_statuses.length >= 0) {
      requestParams.exclude_statuses = params.exclude_statuses
    }
    if (params.factory != null && params.factory !== 'both') {
      requestParams.factory = params.factory
    }
    if (params.order_population != null && params.order_population !== 'standard_custom') {
      requestParams.order_population = params.order_population
    }
    return await apiService.get<OtsJobTypeShippedSalesResponse>('/piq/ots-metrics/job-type-shipped-sales', {
      params: requestParams,
      timeout: 120000,
    })
  }

  /**
   * Get distinct Reference8 values from PrintIQ (for OTS filters).
   * Backend endpoint: GET /api/piq/ots-reference8
   * Uses extended timeout (5 min) – same as getOtsList.
   */
  async getOtsReference8(): Promise<{ data: string[] }> {
    return await apiService.get<{ data: string[] }>('/piq/ots-reference8', {
      timeout: 300000,
    })
  }

  async getOtsDeliveryTypes(): Promise<{ data: string[]; defaults: string[] }> {
    return await apiService.get<{ data: string[]; defaults: string[] }>('/piq/ots-delivery-types', {
      timeout: 300000,
    })
  }

  /**
   * OTS Reasoning tab: override-reason categories/subcategories (already sorted by count desc).
   * Backend: GET /api/piq/ots-reasoning
   */
  async getOtsReasoning(params: {
    date_from: string
    date_to: string
    reference8?: string[]
    delivery_types?: string[]
    exclude_statuses?: string[]
    factory?: 'both' | 'EVT' | 'LAN'
    order_population?: OtsOrderPopulation | 'all'
    on_time?: OtsOnTimeQuickFilter
    department?: string
  }): Promise<OtsReasoningResponse> {
    const requestParams: Record<string, string | string[]> = {
      date_from: params.date_from,
      date_to: params.date_to,
    }
    if (params.reference8 != null) {
      requestParams.reference8 = params.reference8
    }
    if (params.delivery_types != null && params.delivery_types.length > 0) {
      requestParams.delivery_types = params.delivery_types
    }
    if (params.exclude_statuses != null && params.exclude_statuses.length >= 0) {
      requestParams.exclude_statuses = params.exclude_statuses
    }
    if (params.factory != null && params.factory !== 'both') {
      requestParams.factory = params.factory
    }
    if (params.order_population != null && params.order_population !== 'standard_custom') {
      requestParams.order_population = params.order_population
    }
    if (params.on_time != null && params.on_time !== 'both') {
      requestParams.on_time = params.on_time
    }
    if (params.department != null && params.department !== '') {
      requestParams.department = params.department
    }
    return await apiService.get<OtsReasoningResponse>('/piq/ots-reasoning', {
      params: requestParams,
      timeout: 300000,
    })
  }

  /**
   * Manual on-time flag override (2 = late modified, 3 = on-time modified); category/subcategory/
   * department/reason are optional and only meaningful when on_time_flag is 3.
   * PATCH /api/piq/ots-sales/{uuid}/on-time-flag
   */
  async patchOtsOnTimeFlag(
    uuid: string,
    body: {
      on_time_flag: 2 | 3
      category: string | null
      subcategory: string | null
      department: string | null
      reason: string | null
    }
  ): Promise<{ success: boolean; data: Record<string, unknown>; message?: string }> {
    const raw = await apiService.patch<{ success: boolean; data: Record<string, unknown>; message?: string }>(
      `/piq/ots-sales/${uuid}/on-time-flag`,
      body
    )
    assertOtsPiqJsonSuccess(raw, 'Failed to update on-time flag')
    return raw
  }

  /**
   * Bulk manual on-time flag for all rows matching OTS list filters (same as GET /piq/ots-list);
   * category/subcategory/department/reason are optional and only meaningful when on_time_flag is 3.
   * POST /api/piq/ots-sales/bulk-on-time-flag
   */
  async bulkPatchOtsOnTimeFlagByFilters(body: {
    date_from: string
    date_to: string
    reference8?: string[]
    delivery_types?: string[]
    exclude_statuses?: string[]
    factory?: 'both' | 'EVT' | 'LAN'
    search?: string
    on_time_flag: 2 | 3
    category: string | null
    subcategory: string | null
    department: string | null
    reason: string | null
    exclude_uuids?: string[]
    order_population?: 'standard_custom' | 'series' | 'combined' | 'all'
    /** Details tab quick filter; omitted/'both' = no filter. */
    on_time?: OtsOnTimeQuickFilter
    /** OTS Reasoning drill-through row scoping — distinct from category/subcategory/department above, which are the override values to write. */
    filter_category?: string
    filter_subcategory?: string
    filter_department?: string
  }): Promise<{ success: boolean; updated?: number; failed?: number; message?: string }> {
    const raw = await apiService.post<{ success: boolean; updated?: number; failed?: number; message?: string }>(
      '/piq/ots-sales/bulk-on-time-flag',
      body,
      { timeout: 600000 }
    )
    if (!raw || typeof raw !== 'object' || raw.success !== true) {
      const msg =
        raw && typeof raw === 'object' && 'message' in raw && typeof (raw as { message?: string }).message === 'string'
          ? (raw as { message: string }).message
          : 'Bulk on-time flag update failed'
      throw new Error(msg)
    }
    return raw
  }

  /** GET /api/piq/ots-on-time-flag-timeline/{uuid} */
  async getOtsOnTimeFlagTimelineForUuid(uuid: string): Promise<{
    success: boolean
    data: OtsOnTimeFlagTimelineEntry[]
  }> {
    const raw = await apiService.get<{
      success: boolean
      data: OtsOnTimeFlagTimelineEntry[]
    }>(`/piq/ots-on-time-flag-timeline/${uuid}`)
    assertOtsPiqJsonSuccess(raw, 'Failed to load timeline')
    return raw
  }

  /** GET /api/piq/ots-on-time-flag-timeline */
  async getOtsOnTimeFlagTimelineGlobal(params: {
    page?: number
    per_page?: number
    date_from?: string
    date_to?: string
  }): Promise<{
    success: boolean
    data: OtsOnTimeFlagTimelineEntry[]
    pagination: { current_page: number; last_page: number; per_page: number; total: number }
  }> {
    const requestParams: Record<string, string | number> = {}
    if (params.page != null) requestParams.page = params.page
    if (params.per_page != null) requestParams.per_page = params.per_page
    if (params.date_from != null && params.date_from !== '') requestParams.date_from = params.date_from
    if (params.date_to != null && params.date_to !== '') requestParams.date_to = params.date_to
    const raw = await apiService.get<{
      success: boolean
      data: OtsOnTimeFlagTimelineEntry[]
      pagination: { current_page: number; last_page: number; per_page: number; total: number }
    }>('/piq/ots-on-time-flag-timeline', { params: requestParams })
    assertOtsPiqJsonSuccess(raw, 'Failed to load timeline')
    return raw
  }
}

export const machineService = new MachineService()

